import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../shared/subscriptions';
import { usePlanSelectorContext } from '../../contexts/PlanSelectorContext';
import { useAddToast } from '../../contexts/ToastContext';
import { window } from '../../globals';
import { errorMessageFromResponse } from '../../helpers/errors';
import { useMultipleModals } from '../../hooks';
import * as IssuesV2Store from '../../redux/issuesV2';
import * as SavedFiltersStore from '../../redux/savedFilters';
import COLORS from '../../theme/colors';
import DropdownButton from '../generic/DropdownButton';
import Icon, { icons } from '../generic/Icon';
import ReadOnlySection from '../generic/ReadOnlySection';
import Tooltip from '../generic/Tooltip';
import useFetchIssuesAction from '../issues/IssuesInbox/hooks/useFetchIssuesAction';
import type { FailedFetchResponse } from '../../helpers/errors';

import useSavedFiltersPermissions from './hooks/useSavedFiltersPermissions';
import { DeleteModal, RenameModal, SaveAsModal } from './modals';

type SavedFiltersActionType = 'Reset' | 'Save' | 'Save As...' | 'Rename...' | 'Delete';

type SavedFiltersAction = {
  label: string;
  onSelect: () => void;
  tooltip?: string;
  isDisabled?: boolean;
  isRestricted?: boolean;
};

type SavedFiltersDropdownActionsProps = {
  actions: SavedFiltersAction[];
  //  If a network request is happening, or when none of the options are available.
  isDisabled: boolean;
  hasNotificationDot?: boolean;
  hasAnyEditPermission: boolean;
  onChange: (selectedOption: SavedFiltersActionType) => void;
};

/**
 * SavedFiltersDropdownActions renders the actions for saving/deleting/etc.
 * a saved filter from in the UI.
 */
const SavedFiltersDropdownActions: VoidFunctionComponent<SavedFiltersDropdownActionsProps> = ({
  actions,
  isDisabled,
  hasAnyEditPermission,
  hasNotificationDot,
  onChange,
}) => (
  <Tooltip
    id="saved-filters-actions-dropdown-tooltip"
    placement="top"
    showTooltip={isDisabled || !hasAnyEditPermission}
    //  If the user is able to edit, the actions are disabled because the filters have not been edited.
    tooltip={
      hasAnyEditPermission
        ? 'Modify filters in order to save them'
        : 'You do not have permission to modify saved filters.'
    }
  >
    <div>
      <DropdownButton
        id="saved-filters-actions-dropdown"
        title={
          <span style={{ fontSize: '16px' }}>
            <Icon icon={icons.sliders} />
          </span>
        }
        disabled={isDisabled || !hasAnyEditPermission}
        size="large"
        onSelect={onChange}
        hasNotificationDot={hasAnyEditPermission && hasNotificationDot}
        testId="saved-filters-actions-dropdown"
        noCaret
        pullRight
      >
        {actions.map(({ label, isDisabled: isActionDisabled, tooltip, isRestricted: isActionRestricted }) => (
          <DropdownButton.Item key={label} eventKey={label} disabled={isActionDisabled && !isActionRestricted}>
            <Tooltip
              id={`saved-filters-action-tooltip--${label}`}
              placement="top"
              showTooltip={tooltip && isActionDisabled && !isActionRestricted}
              tooltip={tooltip}
            >
              <div
                className="width-full"
                style={
                  !isActionDisabled && !isActionRestricted && label === 'Delete'
                    ? { color: COLORS.fossaRed }
                    : undefined
                }
                data-test-id={`saved-filters-action--${label}`}
              >
                <ReadOnlySection isReadOnly={isActionRestricted} isClickable>
                  {label}
                </ReadOnlySection>
              </div>
            </Tooltip>
            {isActionRestricted && <Icon icon={icons.lock} />}
          </DropdownButton.Item>
        ))}
      </DropdownButton>
    </div>
  </Tooltip>
);

/**
 * Container component for the saved filter dropdown actions.
 * Responsible for hooking up the UI to Redux and managing modals etc.
 */
const SavedFiltersDropdownActionsContainer: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const hasFilterChanged = useSelector(SavedFiltersStore.selectors.getHasChanged);
  const currentFilter = useSelector((state) => SavedFiltersStore.selectors.getCurrentFilterByCategory(state, category));
  const { loading: isLoading, errors } = useSelector(SavedFiltersStore.selectors.getSaveOrDeleteAsyncStatus);
  const hasSearchOrFilterApplied = useSelector(IssuesV2Store.selectors.getHasSearchOrFilterApplied);
  const canSave = useSelector((state) => SavedFiltersStore.selectors.getCanSave(state, category));
  const resetAllFilters = useFetchIssuesAction(IssuesV2Store.actions.resetAllFilters);
  //  Lists the various permissions which a user might have for saved filters (ie: Create, Edit, Delete, etc.)
  const permissions = useSavedFiltersPermissions();
  const isLoadingOrNoCurrentFilter = isLoading || !currentFilter;
  //  Show the "notification dot" if it's not loading AND there are changes
  const hasNotificationDot = !isLoading && canSave && hasFilterChanged;

  //  Modals states
  const { saveAsModal, renameModal, deleteModal } = useMultipleModals('saveAs', 'rename', 'delete');

  // Toasts
  const addToast = useAddToast();

  const { showPlanSelectorModal } = usePlanSelectorContext();

  //  Actions
  const onReset = () => {
    //  If there are unsaved changes but no currentFilter, reset ALL the filters to initial.
    if (currentFilter === null) {
      resetAllFilters();
      //  Reset the current filter to "unfiltered"
      // dispatch(SavedFiltersStore.actions.setCurrentFilter(null));
    } else {
      //  ...otherwise reset back to the currentFilter
      dispatch(SavedFiltersStore.actions.setCurrentFilter(currentFilter.id));
    }
  };

  const onSave = async () => {
    try {
      await dispatch(SavedFiltersStore.actions.saveFilter());
      //  Show the notification
      addToast({ type: 'info', content: 'Filter saved.' });
    } catch (error) {
      const message = errorMessageFromResponse(error as FailedFetchResponse);
      //  Show an error notification
      addToast({ type: 'error', content: message });
    }
  };

  const onSaveAs = async (name: string) => {
    await dispatch(SavedFiltersStore.actions.saveFilterAs(name));
    //  Close the modal
    saveAsModal.onClose();
    //  Show the notification
    addToast({ type: 'info', content: 'Filter saved.' });
  };

  const onRename = async (name: string) => {
    await dispatch(SavedFiltersStore.actions.renameFilter(name));
    //  Close the modal
    renameModal.onClose();
    //  Show the notification
    addToast({ type: 'info', content: 'Filter renamed.' });
  };

  const onDelete = async () => {
    if (currentFilter) {
      //  Awaited so the modal stays open until we do the delete.
      await dispatch(SavedFiltersStore.actions.deleteFilter());
    }
    //  Close the modal
    deleteModal.onClose();
    //  Show the notification
    addToast({ type: 'info', content: 'Filter deleted.' });
  };

  //  The save button can be disabled for various reasons, this determines the tooltip for it.
  const getSaveTooltip = () => {
    if (!permissions.canEdit) {
      return 'You do not have permission to edit saved filters.';
    }
    return currentFilter === null
      ? 'Protected filters may not be saved over.'
      : 'Make changes to the filter in order to save them.';
  };

  const shouldRestrictPremiumActions = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  const withPremiumCheck = (shouldRestrict: boolean, actionFn: () => void) => {
    if (shouldRestrict) {
      return showPlanSelectorModal;
    }

    return actionFn;
  };

  //  Later we use the key as the label, so we omit it from the type here.
  const actions: Record<SavedFiltersActionType, Omit<SavedFiltersAction, 'label'>> = {
    Reset: {
      onSelect: onReset,
      isDisabled: !hasSearchOrFilterApplied,
    },
    Save: {
      onSelect: withPremiumCheck(shouldRestrictPremiumActions, onSave),
      isDisabled: !permissions.canEdit || isLoadingOrNoCurrentFilter || !canSave,
      tooltip: getSaveTooltip(),
      isRestricted: shouldRestrictPremiumActions,
    },
    'Save As...': {
      onSelect: withPremiumCheck(shouldRestrictPremiumActions, saveAsModal.onOpen),
      isDisabled: !permissions.canCreate || isLoading || !(hasSearchOrFilterApplied || canSave),
      tooltip: !permissions.canCreate ? 'You do not have permission to create saved filters.' : undefined,
      isRestricted: shouldRestrictPremiumActions,
    },
    'Rename...': {
      onSelect: withPremiumCheck(shouldRestrictPremiumActions, renameModal.onOpen),
      isDisabled: !permissions.canEdit || isLoadingOrNoCurrentFilter,
      tooltip: !permissions.canEdit
        ? 'You do not have permission to edit saved filters.'
        : 'Protected filters may not be renamed.',
      isRestricted: shouldRestrictPremiumActions,
    },
    Delete: {
      onSelect: withPremiumCheck(shouldRestrictPremiumActions, deleteModal.onOpen),
      isDisabled: !permissions.canDelete || isLoadingOrNoCurrentFilter,
      tooltip: !permissions.canDelete
        ? 'You do not have permission to delete saved filters.'
        : 'Protected filters may not be deleted.',
    },
  };

  //  Find the onSelect function from actions and call it.
  const onChange = (label: SavedFiltersActionType) => actions[label].onSelect();

  //  Determines if the dropdown button itself should be disabled.
  const areAllActionsDisabled = Object.values(actions).every(({ isDisabled }) => isDisabled);

  return (
    <>
      <SavedFiltersDropdownActions
        actions={Object.entries(actions).map(([label, props]) => ({ ...props, label }))}
        isDisabled={areAllActionsDisabled}
        hasNotificationDot={hasNotificationDot}
        hasAnyEditPermission={permissions.canAny}
        onChange={onChange}
      />
      {saveAsModal.isOpen && (
        <SaveAsModal
          isDisplayed={saveAsModal.isOpen}
          isSaving={isLoading}
          errors={errors}
          onHide={saveAsModal.onClose}
          onSave={onSaveAs}
        />
      )}
      {renameModal.isOpen && (
        <RenameModal
          currentName={currentFilter?.name ?? ''}
          isDisplayed={renameModal.isOpen}
          isSaving={isLoading}
          errors={errors}
          onHide={renameModal.onClose}
          onRename={onRename}
        />
      )}
      {deleteModal.isOpen && (
        <DeleteModal
          isDisplayed={deleteModal.isOpen}
          isDeleting={isLoading}
          errors={errors}
          onHide={deleteModal.onClose}
          onDelete={onDelete}
        />
      )}
    </>
  );
};

export default SavedFiltersDropdownActionsContainer;
